import { helpSections, helpConfig } from './helpData.js';

/**
 * HelpUI manages the in-app help system modal
 * Provides keyboard-accessible help documentation with categorized sections
 *
 * @class
 * @example
 * import { helpUI } from './help/helpUI.js';
 *
 * // Show help modal programmatically
 * helpUI.show();
 *
 * // Hide help modal
 * helpUI.hide();
 *
 * // Check if modal is visible
 * if (helpUI.isVisible) {
 *   console.log('Help is currently displayed');
 * }
 */
export class HelpUI {
    /**
     * Initialize the HelpUI manager
     * Sets up initial state for help modal visibility
     *
     * @constructor
     */
    constructor() {
        /**
         * Current visibility state of the help modal
         * @type {boolean}
         * @public
         */
        this.isVisible = false;

        /**
         * Reference to the help modal DOM element (null when hidden)
         * @type {HTMLElement|null}
         * @private
         */
        this.helpModal = null;
    }

    /**
     * Display the help modal overlay
     * Creates and renders the help modal if not already visible
     * Registers keyboard event listeners for modal interaction
     *
     * @returns {void}
     * @public
     *
     * @example
     * // Show help modal
     * helpUI.show();
     *
     * @fires HelpUI#keydown - ESC key closes modal
     */
    show() {
        if (this.isVisible) return;

        this.createHelpModal();
        this.isVisible = true;
    }

    /**
     * Hide and remove the help modal from the DOM
     * Cleans up modal element and resets visibility state
     * Also removes event listeners to prevent memory leaks
     *
     * @returns {void}
     * @public
     *
     * @example
     * // Hide help modal
     * helpUI.hide();
     */
    hide() {
        if (!this.isVisible) return;

        if (this.helpModal) {
            this.helpModal.remove();
            this.helpModal = null;
        }
        this.isVisible = false;
    }

    /**
     * Create and render the help modal structure
     * Builds the complete modal DOM with header, content sections, and footer
     * Adds event listeners for close actions (click outside, close button, ESC key)
     *
     * @private
     * @returns {void}
     *
     * @description
     * Modal structure:
     * - Full-screen backdrop (click to close)
     * - Centered content container (90vw x 90vh max)
     * - Header with title and close button
     * - Scrollable content area with help sections
     * - Footer with version info and keyboard hint
     */
    createHelpModal() {
        // Create modal backdrop
        this.helpModal = document.createElement('div');
        this.helpModal.id = 'helpModal';
        this.helpModal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000003;
            font-family: Arial, sans-serif;
        `;

        // Create modal content
        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
            background: #1a1a1a;
            border: 2px solid #4CAF50;
            border-radius: 8px;
            max-width: 90vw;
            max-height: 90vh;
            overflow: hidden;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
            display: flex;
            flex-direction: column;
        `;

        // Create header
        const header = document.createElement('div');
        header.style.cssText = `
            background: rgba(0, 0, 0, 0.9);
            padding: 20px;
            border-bottom: 2px solid #4CAF50;
            display: flex;
            justify-content: space-between;
            align-items: center;
            height: 60px;
            box-sizing: border-box;
        `;

        const title = document.createElement('h2');
        title.textContent = helpConfig.title;
        title.style.cssText = `
            color: #4CAF50;
            margin: 0;
            font-size: 18px;
            font-weight: normal;
        `;

        const closeButton = document.createElement('button');
        closeButton.textContent = '✕';
        closeButton.style.cssText = `
            background: transparent;
            color: #4CAF50;
            border: 1px solid #4CAF50;
            border-radius: 4px;
            width: 30px;
            height: 30px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
        `;
        closeButton.onmouseover = () => {
            closeButton.style.background = '#4CAF50';
            closeButton.style.color = 'black';
        };
        closeButton.onmouseout = () => {
            closeButton.style.background = 'transparent';
            closeButton.style.color = '#4CAF50';
        };
        closeButton.onclick = () => this.hide();

        header.appendChild(title);
        header.appendChild(closeButton);

        // Create scrollable content area
        const contentArea = document.createElement('div');
        contentArea.style.cssText = `
            padding: 20px;
            overflow-y: auto;
            max-height: 70vh;
            color: white;
        `;

        // Create help sections
        Object.entries(helpSections).forEach(([, section]) => {
            const sectionDiv = this.createHelpSection(section);
            contentArea.appendChild(sectionDiv);
        });

        // Create footer
        const footer = document.createElement('div');
        footer.style.cssText = `
            background: rgba(0, 0, 0, 0.9);
            padding: 15px 20px;
            border-top: 2px solid #4CAF50;
            text-align: center;
            color: #ccc;
            font-size: 12px;
        `;
        footer.innerHTML = `
            <div>Version ${helpConfig.version} • Last Updated: ${helpConfig.lastUpdated}</div>
            <div style="margin-top: 5px;">Press <strong>H</strong> or <strong>?</strong> anytime to show this help</div>
        `;

        // Assemble modal
        modalContent.appendChild(header);
        modalContent.appendChild(contentArea);
        modalContent.appendChild(footer);
        this.helpModal.appendChild(modalContent);

        // Add event listeners
        this.helpModal.onclick = (e) => {
            if (e.target === this.helpModal) {
                this.hide();
            }
        };

        document.addEventListener('keydown', this.handleKeyDown.bind(this));

        // Add to DOM
        document.body.appendChild(this.helpModal);
    }

    /**
     * Create a DOM element for a single help section
     * Renders section title and items in a styled container
     *
     * @private
     * @param {Object} section - Help section data from helpData.js
     * @param {string} section.title - Section title (e.g., "📝 Frame Editing")
     * @param {Array<{action: string, description: string}>} section.items - Help items
     * @returns {HTMLDivElement} Rendered section element ready to append to modal
     *
     * @example
     * // Internal usage within createHelpModal()
     * const section = {
     *   title: "📝 Frame Editing",
     *   items: [
     *     { action: "Left Click Frame", description: "Select and manipulate frame" },
     *     { action: "Drag Frame Body", description: "Move frame position" }
     *   ]
     * };
     * const sectionElement = this.createHelpSection(section);
     */
    createHelpSection(section) {
        const sectionDiv = document.createElement('div');
        sectionDiv.style.cssText = `
            margin-bottom: 20px;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 4px;
            padding: 15px;
            border: 1px solid rgba(76, 175, 80, 0.3);
        `;

        const sectionTitle = document.createElement('h3');
        sectionTitle.textContent = section.title;
        sectionTitle.style.cssText = `
            color: #4CAF50;
            margin: 0 0 12px 0;
            font-size: 16px;
            font-weight: normal;
        `;

        const itemsList = document.createElement('div');
        itemsList.style.cssText = `
            display: grid;
            gap: 10px;
        `;

        section.items.forEach(item => {
            const itemDiv = document.createElement('div');
            itemDiv.style.cssText = `
                display: flex;
                align-items: flex-start;
                gap: 15px;
                padding: 8px 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            `;

            const actionSpan = document.createElement('span');
            actionSpan.textContent = item.action;
            actionSpan.style.cssText = `
                color: #4CAF50;
                font-weight: bold;
                font-family: 'Courier New', monospace;
                min-width: 150px;
                font-size: 13px;
            `;

            const descSpan = document.createElement('span');
            descSpan.textContent = item.description;
            descSpan.style.cssText = `
                color: white;
                flex: 1;
                line-height: 1.4;
                font-size: 13px;
            `;

            itemDiv.appendChild(actionSpan);
            itemDiv.appendChild(descSpan);
            itemsList.appendChild(itemDiv);
        });

        sectionDiv.appendChild(sectionTitle);
        sectionDiv.appendChild(itemsList);
        return sectionDiv;
    }

    /**
     * Handle keyboard events when help modal is visible
     * Closes modal on ESC key press, prevents default browser behavior
     *
     * @private
     * @param {KeyboardEvent} e - Keyboard event object
     * @returns {void}
     *
     * @listens document#keydown
     *
     * @example
     * // Event handler automatically registered when modal is shown
     * // User presses ESC key → modal closes
     */
    handleKeyDown(e) {
        if (this.isVisible && e.key === 'Escape') {
            e.preventDefault();
            this.hide();
        }
    }
}

/**
 * Global singleton instance of HelpUI
 * Use this instance throughout the application for consistent help modal management
 *
 * @type {HelpUI}
 * @constant
 * @public
 *
 * @example
 * import { helpUI } from './help/helpUI.js';
 *
 * // Register keyboard shortcut in main.js
 * document.addEventListener('keydown', (e) => {
 *   if (e.key === 'h' || e.key === 'H' || e.key === '?') {
 *     helpUI.show();
 *   }
 * });
 */
export const helpUI = new HelpUI();